import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Form, {
  ButtonItem,
  SimpleItem,
  RequiredRule,
  GroupItem,
  Label,
  TabbedItem,
  TabPanelOptions,
  Tab,
} from "devextreme-react/form";
import "devextreme-react/text-area";
import notify from "devextreme/ui/notify";
import { useHistory, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { loadMessages, formatMessage, locale } from "devextreme/localization";
import FileSelectPopup from "../../components/file/FileSelectPopup";
import PhotosList from "../../components/website/album/PhotosList";
import VideosList from "../../components/website/album/VideosList";
import { WEBSITE_URL } from "../../utils/constants";
import dictionary from "../../data/dictionary";
import useWindowDimensions from "../../hook/useWindowDimensions";
import { AlbumMV, AlbumVM } from "../../model/Album";

loadMessages(dictionary);

const tabsMock = [
  {
    id: 0,
    text: "vi-VN",
    icon: "globe",
    content: "Vietnamese",
  },
];

const AlbumDetailContainer = ({
  accessToken,
  languages,
  currentLanguage,
  currentWebsite,
}) => {
  locale(currentLanguage.substring(0, 2));
  const history = useHistory();
  const params = useParams();
  const [album, setAlbum] = useState({});
  const [tabs, setTabs] = useState(tabsMock);
  const { height } = useWindowDimensions();

  useEffect(() => {
    const _tabs = languages.map((item, index) => ({
      id: index,
      text: item.languageId,
      icon: "globe",
      name: item.name,
    }));
    setTabs(_tabs);
  }, [languages]);

  useEffect(() => {
    if (!params.albumId) return;
    fetch(`${WEBSITE_URL}Albums/${currentWebsite}/${params.albumId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Access-Control-Allow-Origin": "*",
      },
    }).then((result) =>
      result
        .json()
        .then((json) => {
          if (result.ok) {
            setAlbum(AlbumVM(json.data, languages));
            return;
          }
          throw json.Message;
        })
        .catch(() => {
          if (result.status === 401) {
            history.push("/expiredToken");
          }
        })
    );
  }, []);

  const updateAlbum = () => {
    const _method = album.id ? "PUT" : "POST";
    const _url = `${WEBSITE_URL}Albums/${currentWebsite}/${album.id ? `/${album.id}` : ""}`;

    return fetch(_url, {
      method: _method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Accept-Language": currentLanguage,
      },
      body: AlbumMV(album, languages),
    }).then((result) =>
      result
        .json()
        .then((json) => {
          if (result.ok) {
            notify(
              {
                message: json.message,
                position: {
                  my: "center top",
                  at: "center top",
                },
              },
              "success",
              3000
            );
            setAlbum({ ...album, concurrencyStamp: json.data });
            return;
          }
          throw json.Message;
        })
        .catch(() => {
          if (result.status === 401) {
            history.push("/expiredToken");
          }
        })
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateAlbum();
  };

  const buttonOptions = {
    text: "Update",
    icon: "save",
    type: "default",
    stylingMode: "contained",
    useSubmitBehavior: true,
  };

  const backButtonOptions = {
    type: "back",
    onClick: () => {
      history.goBack();
    },
  };

  const updateLogo = (e) => {
    setAlbum((prevState) => ({
      ...prevState,
      logo: e,
    }));
  };

  return (
    <div
      className="form-container"
      style={{ overflow: "auto", height: height - 56, paddingBottom: 20 }}
    >
      <Toolbar>
        <Item location="before" widget="dxButton" options={backButtonOptions} />
        <Item location="center" locateInMenu="never" text="Thông tin Album" />
      </Toolbar>
      <form action="your-action" onSubmit={handleSubmit}>
        <Form
          formData={album}
          readOnly={false}
          showColonAfterLabel={true}
          colCount={2}
          showValidationSummary={true}
          validationGroup="customerData"
        >
          <GroupItem caption="Đa ngôn ngữ">
            <TabbedItem>
              <TabPanelOptions deferRendering={false} />
              {tabs.map((item) => {
                return (
                  <Tab key={item.id} title={item.name} icon={item.icon}>
                    <SimpleItem
                      dataField={`name-${item.text}`}
                      editorType="dxTextBox"
                    >
                      <Label text={formatMessage("name")} />
                      <RequiredRule />
                    </SimpleItem>

                    <SimpleItem
                      dataField={`description-${item.text}`}
                      editorType="dxTextArea"
                    >
                      <Label text={formatMessage("description")} />
                    </SimpleItem>

                    <SimpleItem
                      dataField={`metaTitle-${item.text}`}
                      editorType="dxTextBox"
                    >
                      <Label text={formatMessage("metaTitle")} />
                    </SimpleItem>

                    <SimpleItem
                      dataField={`metaKeyword-${item.text}`}
                      editorType="dxTextBox"
                    >
                      <Label text={formatMessage("metaKeyword")} />
                    </SimpleItem>

                    <SimpleItem
                      dataField={`metaDescription-${item.text}`}
                      editorType="dxTextArea"
                    >
                      <Label text={formatMessage("metaDescription")} />
                    </SimpleItem>
                  </Tab>
                );
              })}
            </TabbedItem>
          </GroupItem>

          <GroupItem caption="Other">
            <SimpleItem dataField="seoLink" editorType="dxTextBox">
              <Label text={formatMessage("seoLink")} />
            </SimpleItem>
            <SimpleItem dataField="isActive" editorType="dxCheckBox">
              <Label text={formatMessage("isActive")} />
            </SimpleItem>
            <SimpleItem dataField="isHomePage" editorType="dxCheckBox">
              <Label text={formatMessage("isHomePage")} />
            </SimpleItem>
            <SimpleItem dataField="isHot" editorType="dxCheckBox">
              <Label text={formatMessage("isHot")} />
            </SimpleItem>
            <SimpleItem>
              <Label text="Logo" />
              <FileSelectPopup
                value={album.thumbnail}
                imgHeight={100}
                onValueChanged={updateLogo}
              />
            </SimpleItem>
          </GroupItem>

          <ButtonItem
            horizontalAlignment="center"
            colSpan={2}
            buttonOptions={buttonOptions}
          />
        </Form>
      </form>
      <hr />
     {params.albumId && <>
      {album.type === 0 ? (
        <PhotosList
          languages={languages}
          currentLanguage={currentLanguage}
          accessToken={accessToken}
          websiteId={currentWebsite}
          albumId={params.albumId}
        />
      ) : (
        <VideosList
          languages={languages}
          currentLanguage={currentLanguage}
          accessToken={accessToken}
          websiteId={currentWebsite}
          albumId={params.albumId}
        />
      )}
      </>}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    accessToken: state.auth.data.token,
    currentLanguage: state.setting.currentLanguage,
    languages: state.setting.languages,
    currentWebsite: state.website.data.currentWebsite,
  };
};

AlbumDetailContainer.propTypes = {
  accessToken: PropTypes.string,
  languages: PropTypes.array,
  currentLanguage: PropTypes.string,
  currentWebsite: PropTypes.string,
};

export default connect(mapStateToProps, null)(AlbumDetailContainer);
